import { z } from 'zod'
import { AppError } from '@/shared/domain/AppError'
import type { ClientRecord } from '../../domain/client'

export const legacyClientRowSchema = z.object({
  id: z.string(),
  numero: z.string().nullable(),
  prenom: z.string().nullable(),
  nom: z.string().nullable(),
  entreprise: z.string().nullable(),
  telephone: z.string().nullable(),
  telephone_mobile: z.string().nullable().optional(),
  courriel: z.string().nullable(),
  adresse: z.string().nullable(),
  adresse_ligne2: z.string().nullable().optional(),
  ville: z.string().nullable(),
  province: z.string().nullable(),
  code_postal: z.string().nullable(),
  latitude: z.coerce.number().nullable(),
  longitude: z.coerce.number().nullable(),
  type_client: z.enum(['residentiel', 'commercial']).nullable(),
  statut: z.enum(['actif', 'suspendu', 'inactif', 'archive']),
  statut_motif: z.string().nullable().optional(),
  langue: z.enum(['francais', 'anglais']),
  created_at: z.string(),
  updated_at: z.string(),
  deleted_at: z.string().nullable(),
})

export type LegacyClientRow = z.infer<typeof legacyClientRowSchema>

function statutToClientStatus(row: LegacyClientRow): ClientRecord['status'] {
  if (row.deleted_at) return 'ARCHIVED'
  switch (row.statut) {
    case 'actif':
      return 'ACTIVE'
    case 'suspendu':
      return 'SUSPENDED'
    case 'archive':
      return 'ARCHIVED'
    case 'inactif':
      return 'INACTIVE'
  }
}

function optionalText(value: string | null): string | undefined {
  const normalized = value?.trim()
  if (!normalized) return undefined
  return normalized
}

export function mapLegacyClient(row: LegacyClientRow): ClientRecord {
  const firstName = optionalText(row.prenom)
  const lastName = optionalText(row.nom)
  const companyName = optionalText(row.entreprise)
  const phone = optionalText(row.telephone)
  const number = optionalText(row.numero)
  if (!number || !phone || !row.type_client) {
    throw new AppError('UNEXPECTED_ERROR', 'Une ligne Client legacy ne respecte pas le contrat V3.', {
      clientId: row.id,
      missing: [
        !number ? 'numero' : null,
        !phone ? 'telephone' : null,
        !row.type_client ? 'type_client' : null,
      ].filter(Boolean),
    })
  }
  const displayName = companyName ?? [firstName, lastName].filter(Boolean).join(' ')
  if (!displayName) {
    throw new AppError('UNEXPECTED_ERROR', 'Un Client legacy ne possède aucun nom exploitable.', {
      clientId: row.id,
    })
  }
  const line1 = optionalText(row.adresse)
  const line2 = optionalText(row.adresse_ligne2 ?? null)
  const city = optionalText(row.ville)
  const postalCode = optionalText(row.code_postal)
  const province = optionalText(row.province) ?? 'QC'
  const email = optionalText(row.courriel)?.toLocaleLowerCase('fr-CA')
  const mobilePhone = optionalText(row.telephone_mobile ?? null)
  const statutMotif = optionalText(row.statut_motif ?? null)
  if (email && !z.email().safeParse(email).success) {
    throw new AppError('UNEXPECTED_ERROR', 'Un Client legacy possède un courriel invalide.', {
      clientId: row.id,
    })
  }
  const address =
    line1 && city && postalCode
      ? {
          line1,
          ...(line2 ? { line2 } : {}),
          city,
          province,
          postalCode,
          ...(row.latitude === null ? {} : { latitude: row.latitude }),
          ...(row.longitude === null ? {} : { longitude: row.longitude }),
        }
      : undefined
  return {
    id: row.id,
    number,
    type: row.type_client === 'commercial' ? 'COMMERCIAL' : 'RESIDENTIAL',
    status: statutToClientStatus(row),
    language: row.langue === 'anglais' ? 'EN' : 'FR',
    displayName,
    ...(firstName ? { firstName } : {}),
    ...(lastName ? { lastName } : {}),
    ...(companyName ? { companyName } : {}),
    phone,
    ...(mobilePhone ? { mobilePhone } : {}),
    ...(email ? { email } : {}),
    ...(address ? { address } : {}),
    ...(statutMotif ? { statutMotif } : {}),
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  }
}
